import {RouteLocationRaw} from 'vue-router'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {IResourceItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceItemResponseInstance'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {ResourceTypeDataMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeDataMap'
import {
  getResourceRouteFromValue,
  ResourceRouteInfo,
} from '@toolify/client/src/modules/resources/utils/getResourceRouteFromValue'

type LastVisitedResourceRouteOptions = {
  workspaceName: string
  spaceId: string
  resourceItems: IResourceItemResponseInstance[]
}

export function getLastVisitedResourceRouteForSpace(
  options: LastVisitedResourceRouteOptions,
): ResourceRouteInfo | null {
  const {workspaceName, spaceId, resourceItems} = options
  if(!workspaceName) {
    return null
  }

  const localPreferenceStore = useLocalPreferenceStore()
  const preferenceValues = [
    localPreferenceStore.get(LocalPreferenceType.LastVisitedResourceBySpaceId, spaceId),
    localPreferenceStore.get(LocalPreferenceType.LastVisitedResource, workspaceName),
  ]

  for(const preferenceValue of preferenceValues) {
    if(!preferenceValue) {
      continue
    }

    const lastVisitedRoute = getResourceRouteFromValue(preferenceValue, workspaceName)
    if(!lastVisitedRoute) {
      continue
    }

    const resourceItem = resourceItems.find(
      (item) => item.resourceEntity.resourceId === lastVisitedRoute.resourceId
        && item.resourceEntity.resourceType === lastVisitedRoute.resourceType,
    )

    if(!resourceItem) {
      continue
    }

    if(!resourceItem.resourceSpaceId || resourceItem.resourceSpaceId === spaceId) {
      return lastVisitedRoute
    }
  }

  return null
}

function buildRouteFromResourceItem(
  workspaceName: string,
  resourceItem: IResourceItemResponseInstance,
): RouteLocationRaw | null {
  const resourceTypeDataFn = ResourceTypeDataMap.get(resourceItem.resourceEntity.resourceType)
  if(!resourceTypeDataFn) {
    return null
  }
  const resourceData = resourceTypeDataFn(resourceItem.resourceEntity)
  if(!resourceData?.link || typeof resourceData.link !== 'object' || !('name' in resourceData.link)) {
    return null
  }
  return {
    name: resourceData.link.name,
    params: {
      workspaceName,
      ...(resourceData.link.params || {}),
    },
  }
}

function getFirstResourceRouteInSpace(
  workspaceName: string,
  spaceId: string,
  resourceItems: IResourceItemResponseInstance[],
): RouteLocationRaw | null {
  const first = resourceItems
    .filter((item) => item.resourceSpaceId === spaceId)
    .sort((a, b) => a.position - b.position)[0]
  if(!first) {
    return null
  }
  return buildRouteFromResourceItem(workspaceName, first)
}

export function getFirstResourceRouteAnywhere(
  workspaceName: string,
  resourceItems: IResourceItemResponseInstance[],
): RouteLocationRaw | null {
  const first = [...resourceItems].sort((a, b) => a.position - b.position)[0]
  if(!first) {
    return null
  }
  return buildRouteFromResourceItem(workspaceName, first)
}

export function getTargetRouteForSpace(
  options: LastVisitedResourceRouteOptions,
): RouteLocationRaw {
  const {workspaceName, spaceId, resourceItems} = options
  const lastVisited = getLastVisitedResourceRouteForSpace(options)
  if(lastVisited?.route) {
    return lastVisited.route
  }
  const firstRoute = getFirstResourceRouteInSpace(workspaceName, spaceId, resourceItems)
  if(firstRoute) {
    return firstRoute
  }
  return {
    name: RouteName.WorkspaceHome,
    params: {workspaceName},
  }
}
